import { Constants } from "../../../model/Constants";

/**
 *
 * @return {Object} engagement params
 */
export function getChatEventInfo(sdkInstance) {
    const chatParams = sdkInstance.getChatParams();
    const eventChatInfo = {
        chatID: chatParams.getChatID(),
        siteID: chatParams.siteID,
        businessUnitID: chatParams.businessUnitID,
        agentGroupID: chatParams.agId,
        customerID: chatParams.getCustomerID(),
        pageID: chatParams.getLaunchPageId(),
        businessRuleID: chatParams.brID,
        agentID: chatParams.agentID,
        incAssignmentID: chatParams.incAssignmentID,
        availableAgentAttributes: chatParams.agentAttributes,
        sessionID: chatParams.sessionID,
        visitorAttributes: chatParams.getVisitorAttributes(),
        brAttributes: chatParams.ruleAttributes,
        deviceType: chatParams.getDeviceType(),
        operatingSystemType: chatParams.getOperatingSystemType(),
        browserType: chatParams.getBrowserType(),
        browserVersion: chatParams.getBrowserVersion(),
        countryCode: chatParams.countryCode,
        regionCode: chatParams.regionCode,
        preAssigned: !sdkInstance.isConnected() && !chatParams.agentID,
    };
    return eventChatInfo;
}

/**
 * Creates the content send to customer reporting event object
 * @param nodeID
 * @param name
 * @param questions - array of questions
 * @param questionIds - array of questionIds
 * @param chatParamsObject
 * @param widgetType
 * @return {{_domain: string, "custom.decisiontree.nodeID": string, evt: string, "custom.decisiontree.questions": string, "custom.decisiontree.questionIDs": string, unique_node_id: *, clientTimestamp: number, automatonType: (*|string)}}
 */
export function getContentSeenEvent(jsonSpec, nodeID, name, questions, questionIds, chatParamsObject, widgetType) {
    const viewEvent = {
        _domain: "automaton",
        evt: "contentSentToCustomer",
        surveyVersion: 2,
        unique_node_id: nodeID,
        "custom.decisiontree.nodeID": encodeURIComponent(nodeID || ""),
        "custom.decisiontree.questions": getQsFromArray(questions),
        "custom.decisiontree.questionIDs": encodeURIComponent(questionIds.join(",")),
        clientTimestamp: Date.now(),
        automatonType: widgetType || "guide",
    };

    Object.assign(viewEvent, chatParamsObject);

    if (jsonSpec.widgetID) {
        viewEvent["automatonID"] = jsonSpec.widgetID;
    }

    if (jsonSpec.widgetName) {
        viewEvent["automatonName"] = jsonSpec.widgetName;
    }

    viewEvent["automatonOrigin"] = Constants.RM;

    return viewEvent;
}

/**
 * Converts array of string to comma seperated string with index prepended to it
 * @example test,test
 * @param {Array} ques
 * @return {string}
 */
export function getQsFromArray(ques) {
    const val = ques.join(",");
    return encodeURIComponent(val);
}

/**
 * Creates reporting event object when customer transition action
 * @param {object} jsonSpec
 * @param {string} oNodeID
 * @param {string} name
 * @param {string} oQues
 * @param {string} oAns
 * @param {object} chatParamsObject
 * @param {string} widgetType
 * @return {{_domain: string, "custom.decisiontree.nodeID": string, "custom.decisiontree.answersIDs": string, evt: string, "custom.decisiontree.questions": string, "custom.decisiontree.answers": string, "custom.decisiontree.questionIDs": string, "custom.decisiontree.answerTypes": string, unique_node_id: *, clientTimestamp: number, automatonType: (*|string)}}
 */
export function getCustomerRespondedEvent(jsonSpec, oNodeID, name, oQues, oAns, chatParamsObject, widgetType) {
    const ques = [];
    const ansids = [];
    const ans = [];
    const typs = [];
    const qIds = [];
    oAns.forEach(({ id, val, lbl, type, ansID }) => {
        qIds.push(id);
        ansids.push(ansID);
        ques.push(lbl);
        ans.push(val || null);
        typs.push(type);
    });

    const viewEvent = {
        _domain: "automaton",
        evt: "customerResponded",
        surveyVersion: 2,
        unique_node_id: oNodeID,
        "custom.decisiontree.nodeID": encodeURIComponent(oNodeID || ""),
        "custom.decisiontree.questionIDs": encodeURIComponent(qIds.join(",")),
        "custom.decisiontree.questions": getQsFromArray(ques),
        "custom.decisiontree.answers": encodeURIComponent(ans.join(",")),
        "custom.decisiontree.answerIDs": encodeURIComponent(ansids.join(",")),
        "custom.decisiontree.answerTypes": encodeURIComponent(typs.join(",")),
        clientTimestamp: Date.now(),
        automatonType: widgetType || "guide",
    };

    if (jsonSpec.widgetID) {
        viewEvent["automatonID"] = jsonSpec.widgetID;
    }

    if (jsonSpec.widgetName) {
        viewEvent["automatonName"] = jsonSpec.widgetName;
    }

    viewEvent["automatonOrigin"] = Constants.RM;

    Object.assign(viewEvent, chatParamsObject);
    return viewEvent;
}

/**
 * Creates reporting event object for RM rendering started action.
 * @param {object} jsonSpec
 * @param {string} startedBy
 * @param {string} startedIn
 * @param {object} chatParamsObject
 * @param {string} widgetType
 * @return {{_domain: string, evt: string, automatonStartedBy: *, startedIn: *, type: *, automatonType: *, clientTimestamp: number}}
 */
export function getStartedEvent(jsonSpec, startedBy, startedIn, chatParamsObject, widgetType) {
    const startedEvent = {
        _domain: "automaton",
        evt: "started",
        surveyVersion: 2,
        automatonType: widgetType,
        automatonStartedBy: startedBy,
        startedIn: startedIn,
        type: widgetType,
        clientTimestamp: Date.now(),
    };

    Object.assign(startedEvent, chatParamsObject);

    if (jsonSpec.widgetID) {
        startedEvent["automatonID"] = jsonSpec.widgetID;
    }

    if (jsonSpec.widgetName) {
        startedEvent["automatonName"] = jsonSpec.widgetName;
    }

    startedEvent["automatonOrigin"] = Constants.RM;
    return startedEvent;
}

/**
 * Creates reporting event object when customer completes the RM/Survey widget
 * @param {object} jsonSpec
 * @param {string} completed
 * @param {string} outcome
 * @param {string} isAbandoned
 * @param {object} chatParamsObject
 * @param {string} widgetType
 * @return {{_domain: string, evt: string, "automaton.outcomeType": string, automatonType: (*|string), clientTimestamp: number}}
 */
export function getEndedEvent(jsonSpec, completed, outcome, isAbandoned, chatParamsObject, widgetType) {
    const endedEvent = {
        _domain: "automaton",
        evt: "ended",
        surveyVersion: 2,
        automatonType: widgetType || "guide",
        "automaton.outcomeType": completed ? "Completed" : (isAbandoned ? "Abandoned" : "Refused"), // eslint-disable-line no-nested-ternary
        clientTimestamp: Date.now(),
    };

    if (outcome) {
        endedEvent["automaton.outcome"] = outcome;
    }

    if (jsonSpec.widgetID) {
        endedEvent["automatonID"] = jsonSpec.widgetID;
    }

    if (jsonSpec.widgetName) {
        endedEvent["automatonName"] = jsonSpec.widgetName;
    }

    endedEvent["automatonOrigin"] = Constants.RM;

    Object.assign(endedEvent, chatParamsObject);
    return endedEvent;
}
